// Core packages
import { Injectable } from "@angular/core";

// Third party packages
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";

// Custom packages
import { TranslateService } from "@ngx-translate/core";
import { ListApiResponseV2 } from "src/app/shared/interfaces/_base/api-response.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { HelperService } from "src/app/shared/services/helper.service";

@Injectable({
  providedIn: "root",
})
export class InvoicesService {
  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
    private translateService: TranslateService
  ) {}

  /**
   * Create a new invoice
   *
   * @since 1.0.0
   *
   * @param invoice Invoice data
   * @returns Observable<any>
   */
  create(invoice: any): Observable<any> {
    return this.apiService.apiCall("POST", "invoices", invoice);
  }

  /**
   * Get invoices list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall("GET", "invoices", params);
  }
  
  /**
   * Get an exportation file of invoices
   *
   * @since 1.0.0
   */
  getExport(balanceNumber: number): Observable<any> {
    return this.apiService
      .apiCall("GET", `invoices/export/${balanceNumber}`, {}, { responseType: "blob" })
      .pipe(
        catchError((err) => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }


  /**
   * Get given invoiceId data from back-end
   *
   * @since 1.0.0
   *
   * @param invoiceId ID of the balance to get
   */
  get(invoiceId: string): Observable<any> {
    return this.apiService.apiCall("GET", `invoices/${invoiceId}`).pipe(
      catchError((err) => {
        this.helperService.handleError(err);
        return throwError(err);
      })
    );
  }

  /**
   * Update invoice with given invoiceId with given invoice data
   *
   * @since 1.0.0
   *
   * @param invoiceId ID of invoice to update
   * @param data New balance's data
   */
   updateFields(invoiceId: string, data: object): Observable<any> {
    return this.apiService.apiCall("PATCH", `invoices/${invoiceId}`, data);
  }

  /**
   * Delete permanently a invoice
   *
   * @since 1.0.0
   *
   * @param invoiceId ID of the invoice
   */
  delete(invoiceId: string): Observable<any> {
    return this.apiService.apiCall("DELETE", `invoices/${invoiceId}`);
  }
}
